import React, { useState, useEffect } from "react";
import { 
  Activity, 
  ShieldAlert, 
  CheckCircle2, 
  Clock, 
  Calendar, 
  Award, 
  BookOpen, 
  Sparkles, 
  AlertCircle, 
  ArrowRight, 
  ChevronDown, 
  Play, 
  X, 
  Lock, 
  Mail, 
  Check, 
  HelpCircle,
  FileText,
  MapPin,
  Flame,
  Globe,
  Plus,
  Minus
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

export default function App() {
  // State for landing page fold
  const [showLongScroll, setShowLongScroll] = useState(false);
  
  // State for Registration Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState(1);
  const [qualification, setQualification] = useState("");
  const [profile, setProfile] = useState("");
  const [challenge, setChallenge] = useState("");
  const [isRedirecting, setIsRedirecting] = useState(false);

  // State for Spine Simulator
  const [simulatorState, setSimulatorState] = useState("SITTING"); // SITTING, LIFTING, DECOMPRESSION

  // State for FAQ Accordion
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  // State for Countdown Timer
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Legal Modals
  const [activeLegalModal, setActiveLegalModal] = useState(null); // 'PRIVACY' or 'TERMS'

  // Calculated date of webinar: Sep 19, 2026 11:00 AM IST
  useEffect(() => {
    const targetDate = new Date("2026-09-19T11:00:00+05:30").getTime();

    const calculate = () => {
      const now = new Date().getTime();
      const difference = targetDate - now;

      if (difference < 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      } else {
        const d = Math.floor(difference / (1000 * 60 * 60 * 24));
        const h = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const m = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const s = Math.floor((difference % (1000 * 60)) / 1000);
        setTimeLeft({ days: d, hours: h, minutes: m, seconds: s });
      }
    };

    calculate();
    const interval = setInterval(calculate, 1000);

    return () => clearInterval(interval);
  }, []);

  const openRegistrationModal = () => {
    setModalStep(1);
    setQualification("");
    setProfile("");
    setChallenge("");
    setIsRedirecting(false);
    setIsModalOpen(true);
  };

  const handleQualification = (option) => {
    setQualification(option);
    if (option === "YES") {
      setModalStep(2);
      toast.success("Qualification verified! Moving to next step.");
    } else {
      setModalStep(4); // Disqualification screen
    }
  };

  const handleDetailsSubmit = (e) => {
    e.preventDefault();
    if (!profile || !challenge) {
      toast.error("Please fill in all dropdown options to continue.");
      return;
    }
    setModalStep(3);
    setIsRedirecting(true);
    toast.success("Profile recorded. Preparing redirection...");
    
    // Simulate redirection countdown
    setTimeout(() => {
      setIsRedirecting(false);
    }, 2000);
  };

  const handleFaqToggle = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const triggerScrollUnfold = () => {
    setShowLongScroll(true);
    toast.info("Discover more spinal insights below!");
    setTimeout(() => {
      document.getElementById("unfolded-content")?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // Redirection Link to TagMango Webinar
  const tagmangoUrl = "https://learn.painfree-spine.com/web/checkout/6a9311d92d26b133ba98f342";

  return (
    <div className="min-h-screen bg-ivory text-charcoal flex flex-col font-sans selection:bg-gold/30 selection:text-charcoal relative">
      <Toaster position="top-center" richColors />

      {/* Top Sticky Header */}
      <header className="sticky top-0 z-40 bg-ivory/85 backdrop-blur-md border-b border-gold/25 shadow-sm transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center border border-gold shadow-md">
              <Activity className="w-5 h-5 text-ivory" />
            </div>
            <div>
              <span data-testid="nav-logo" className="font-serif text-lg sm:text-xl font-bold tracking-tight text-primary block">
                PAINFREE<span className="text-gold font-sans font-light">SPINE</span>
              </span>
              <span className="text-[10px] tracking-wider uppercase text-mutedTeal block font-semibold">Masterclass</span>
            </div>
          </div>
          
          <nav className="hidden md:flex items-center gap-6">
            <a 
              href="#curriculum" 
              data-testid="nav-link-curriculum" 
              className="text-sm font-semibold text-mutedTeal hover:text-primary transition-colors"
              onClick={(e) => {
                e.preventDefault();
                if (!showLongScroll) setShowLongScroll(true);
                setTimeout(() => document.getElementById("unfolded-content")?.scrollIntoView({ behavior: "smooth" }), 100);
              }}
            >
              Curriculum
            </a>
            <a 
              href="#bio" 
              className="text-sm font-semibold text-mutedTeal hover:text-primary transition-colors"
              onClick={(e) => {
                e.preventDefault();
                if (!showLongScroll) setShowLongScroll(true);
                setTimeout(() => document.getElementById("bio-section")?.scrollIntoView({ behavior: "smooth" }), 100);
              }}
            >
              About Pooja
            </a>
            <a 
              href="#testimonials" 
              className="text-sm font-semibold text-mutedTeal hover:text-primary transition-colors"
              onClick={(e) => {
                e.preventDefault();
                if (!showLongScroll) setShowLongScroll(true);
                setTimeout(() => document.getElementById("testimonials-section")?.scrollIntoView({ behavior: "smooth" }), 100);
              }}
            >
              Reviews
            </a>
            <a 
              href="#faq" 
              className="text-sm font-semibold text-mutedTeal hover:text-primary transition-colors"
              onClick={(e) => {
                e.preventDefault();
                if (!showLongScroll) setShowLongScroll(true);
                setTimeout(() => document.getElementById("faq-section")?.scrollIntoView({ behavior: "smooth" }), 100);
              }}
            >
              FAQ
            </a>
          </nav>

          <button 
            onClick={openRegistrationModal}
            data-testid="nav-register-cta"
            className="btn-premium bg-primary text-ivory border border-gold/40 px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold tracking-wide uppercase inline-flex items-center gap-1.5"
          >
            Register FREE
            <Sparkles className="w-3.5 h-3.5 text-gold" />
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <main className="flex-grow">
        <section className="relative py-12 sm:py-20 overflow-hidden bg-gradient-to-b from-ivory to-sage/40">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Content Column */}
            <div className="lg:col-span-7 space-y-8 text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/10 border border-gold/40 text-charcoal text-xs font-semibold tracking-wider uppercase">
                <Flame className="w-3 h-3 text-gold animate-pulse" />
                <span data-testid="hero-urgency-badge">Live Masterclass • 19 September Edition</span>
              </div>

              <h1 data-testid="hero-main-heading" className="text-4xl sm:text-5xl lg:text-6xl font-serif text-primary tracking-tight leading-[1.12]">
                Discover the Hidden Reasons Behind Lower-Back Discomfort & Learn a Smarter Way to Support Your Spine
              </h1>

              {/* Subheading strictly conforming to H2 size guidelines: text-base sm:text-lg */}
              <h2 data-testid="hero-sub-heading" className="text-base sm:text-lg text-mutedTeal font-sans leading-relaxed max-w-2xl">
                35–55-year-old IT & corporate professionals dealing with recurring lower-back discomfort, stiffness, or nerve-related discomfort. 
                They want comfortable movement, better spinal health, and freedom from back-related limitations in daily life. 
                This webinar gives them practical guidance through my <strong className="text-primary font-serif">Neuro-Spine Decompression Method™</strong> to understand and support their spine and nerve health.
              </h2>

              <div className="p-4 bg-primary/5 rounded-2xl border border-primary/10 max-w-xl text-xs sm:text-sm text-primary/90 font-medium">
                ⚠️ Limited free seats—reserve your spot now and join the live webinar on <span className="font-bold underline text-gold">19 September at 11AM IST</span>.
              </div>

              {/* Grid of 4 Info Blocks */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl pt-2">
                <div data-testid="hero-info-date" className="p-4 rounded-xl bg-ivory border border-gold/25 shadow-sm text-center">
                  <Calendar className="w-5 h-5 text-gold mx-auto mb-2" />
                  <span className="block text-[10px] tracking-widest text-mutedTeal uppercase font-bold">Date</span>
                  <span className="text-sm font-serif font-bold text-primary">19 September 2026</span>
                </div>
                <div data-testid="hero-info-time" className="p-4 rounded-xl bg-ivory border border-gold/25 shadow-sm text-center">
                  <Clock className="w-5 h-5 text-gold mx-auto mb-2" />
                  <span className="block text-[10px] tracking-widest text-mutedTeal uppercase font-bold">Time</span>
                  <span className="text-sm font-serif font-bold text-primary">11:00 AM IST</span>
                </div>
                <div data-testid="hero-info-language" className="p-4 rounded-xl bg-ivory border border-gold/25 shadow-sm text-center">
                  <Globe className="w-5 h-5 text-gold mx-auto mb-2" />
                  <span className="block text-[10px] tracking-widest text-mutedTeal uppercase font-bold">Language</span>
                  <span className="text-sm font-serif font-bold text-primary">Hinglish / Eng</span>
                </div>
                <div data-testid="hero-info-duration" className="p-4 rounded-xl bg-ivory border border-gold/25 shadow-sm text-center">
                  <Activity className="w-5 h-5 text-gold mx-auto mb-2" />
                  <span className="block text-[10px] tracking-widest text-mutedTeal uppercase font-bold">Duration</span>
                  <span className="text-sm font-serif font-bold text-primary">90 Minutes</span>
                </div>
              </div>

              {/* Countdown Timer */}
              <div className="bg-sage/50 border border-secondary/20 p-5 rounded-2xl max-w-xl">
                <p className="text-xs font-semibold text-mutedTeal tracking-wider uppercase mb-3">Webinar starts in:</p>
                <div data-testid="hero-countdown-timer" className="flex items-center gap-4 text-center">
                  <div>
                    <span className="block text-2xl sm:text-3xl font-serif font-bold text-primary">{timeLeft.days}</span>
                    <span className="text-[10px] tracking-wider uppercase text-mutedTeal font-bold">Days</span>
                  </div>
                  <span className="text-primary font-bold text-xl">:</span>
                  <div>
                    <span className="block text-2xl sm:text-3xl font-serif font-bold text-primary">{timeLeft.hours}</span>
                    <span className="text-[10px] tracking-wider uppercase text-mutedTeal font-bold">Hrs</span>
                  </div>
                  <span className="text-primary font-bold text-xl">:</span>
                  <div>
                    <span className="block text-2xl sm:text-3xl font-serif font-bold text-primary">{timeLeft.minutes}</span>
                    <span className="text-[10px] tracking-wider uppercase text-mutedTeal font-bold">Mins</span>
                  </div>
                  <span className="text-primary font-bold text-xl">:</span>
                  <div>
                    <span className="block text-2xl sm:text-3xl font-serif font-bold text-primary">{timeLeft.seconds}</span>
                    <span className="text-[10px] tracking-wider uppercase text-mutedTeal font-bold">Secs</span>
                  </div>
                </div>
              </div>

              {/* Primary CTA Block */}
              <div className="space-y-3 pt-2">
                <button
                  onClick={openRegistrationModal}
                  data-testid="hero-register-cta"
                  className="btn-premium bg-primary text-ivory text-sm sm:text-base font-bold tracking-wider uppercase px-8 py-4 rounded-full border border-gold/50 shadow-md inline-flex items-center gap-2 w-full sm:w-auto justify-center"
                >
                  Register for FREE Webinar
                  <ArrowRight className="w-5 h-5 text-gold" />
                </button>
                <p className="text-xs text-mutedTeal flex items-center gap-1.5 justify-start pl-2">
                  <ShieldAlert className="w-3.5 h-3.5 text-gold" />
                  Free session. Register now and receive reminders before the session.
                </p>
              </div>
            </div>

            {/* Right Image Column */}
            <div className="lg:col-span-5 relative">
              <div className="relative mx-auto max-w-sm sm:max-w-md lg:max-w-none">
                {/* Accent design rings */}
                <div className="absolute -top-6 -left-6 w-24 h-24 rounded-full border border-gold/20 -z-10 animate-pulse"></div>
                <div className="absolute -bottom-6 -right-6 w-36 h-36 rounded-full border-2 border-primary/10 -z-10"></div>
                
                {/* Image Container with Gold Shadow frame */}
                <div className="relative rounded-3xl overflow-hidden border border-gold/45 shadow-[0_20px_50px_rgba(15,92,94,0.15)] bg-ivory">
                  <img 
                    src="https://images.emergent.sh/de9dd7e3-d96c-4ea0-914d-0dc5158af4c7/0.png"
                    alt="Pooja Chadha Neuro-Spine Decompression Expert & Founder of POOJA NeuroYoga"
                    data-testid="hero-teacher-image"
                    className="w-full h-auto object-cover max-h-[500px]"
                    onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&w=800&q=80"; }}
                  />
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-primary/95 via-primary/80 to-transparent p-6 text-ivory">
                    <p className="font-serif text-lg font-bold text-gold">Pooja Chadha</p>
                    <p className="text-xs text-ivory/80 font-medium tracking-wide">Nerve Decompression Expert & Founder of Pooja Neuro-Yoga</p>
                    <p className="text-[10px] text-gold/90 mt-1 uppercase font-semibold tracking-wider">27+ Years Experience • 1000+ Success Stories</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* Fold Trigger Section */}
        <section className="py-12 text-center bg-sage border-y border-secondary/20">
          <div className="max-w-4xl mx-auto px-4">
            <h3 className="text-xl sm:text-2xl font-serif text-primary mb-4">Want to dive deeper into the Neuro-Spine Decompression Method™?</h3>
            <p className="text-sm sm:text-base text-mutedTeal max-w-xl mx-auto mb-6">
              Learn the exact 3 secrets, anatomical details, science-backed postures, and see reviews of professionals who avoided surgery.
            </p>
            <button
              onClick={triggerScrollUnfold}
              data-testid="fold-reveal-button"
              className="btn-premium bg-primary text-ivory font-semibold px-6 py-3.5 rounded-full border border-gold/45 text-sm uppercase inline-flex items-center gap-2 shadow-md animate-bounce"
            >
              {showLongScroll ? "Spine Method Unfolded" : "Want to know more about this webinar?"}
              <ChevronDown className={`w-4 h-4 text-gold transition-transform duration-300 ${showLongScroll ? "rotate-180" : ""}`} />
            </button>
          </div>
        </section>

        {/* Unfolded Content (reveals when button clicked or scrolled) */}
        <div id="unfolded-content" className={`transition-all duration-700 overflow-hidden ${showLongScroll ? "max-h-[99999px] opacity-100" : "max-h-0 opacity-0 pointer-events-none"}`}>
          
          {/* Proof and Credentials Section */}
          <section data-testid="proof-section" className="py-16 sm:py-24 bg-ivory">
            <div className="max-w-7xl mx-auto px-4 sm:px-8">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="text-xs font-bold text-gold uppercase tracking-widest block mb-2">CLINICAL & THERAPEUTIC RECORD</span>
                <h3 className="text-2xl sm:text-3xl font-serif text-primary">Why Learn From Pooja Chadha</h3>
                <div className="w-16 h-0.5 bg-gold mx-auto mt-4"></div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <div className="lg:col-span-5">
                  <div className="rounded-2xl overflow-hidden border border-gold/30 shadow-lg">
                    <img 
                      src="https://images.unsplash.com/photo-1539815208687-a0f05e15d601?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHw3fHxzcGluZSUyMHRoZXJhcHklMjBkb2N0b3IlMjBwb3N0dXJlJTIwd2VsbG5lc3MlMjBpbnN0cnVjdG9yJTIwbWFzdGVyY2xhc3N8ZW58MHx8fHwxNzg4OTQ4NDM2fDA&ixlib=rb-4.1.0&q=85" 
                      alt="Clinical spine posture alignment study" 
                      className="w-full h-auto object-cover"
                    />
                  </div>
                </div>
                <div className="lg:col-span-7 space-y-4">
                  {[
                    "27+ years of experience as an offline Yoga Trainer & 6 years practicing NeuroYoga and nerve-decompression online and offline.",
                    "Specialised in non-surgical Neuro Decompression Therapy for severe spinal issues.",
                    "Certified Naturopathy & Neurotherapy practitioner, running a specialized spinal-care clinic.",
                    "Helped 1000+ people overcome slip disc, sciatica, disc desiccation, and severe lower-back problems.",
                    "Helped 50+ people avoid recommended spinal surgeries through personalized, safe, non-invasive therapeutic practices.",
                    "Awarded 'Best Entrepreneur of the Year 2024' in Health Service Business by Women Leaders Forum.",
                    "Best Yoga Teacher Award — Patanjali Yog Samiti, Haridwar (2019).",
                    "Tehsil In-Charge, Mahila Patanjali Samiti, Haridwar.",
                    "Master's in Nerve Decompression — Meerut Medical College, Meerut.",
                    "Yoga Teacher & Evaluator, Level 4 Certification — Yoga Professional, Ministry of AYUSH, Government of India."
                  ].map((point, index) => (
                    <div key={index} className="flex gap-3 items-start text-left">
                      <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-secondary/15 flex items-center justify-center">
                        <Check className="w-3 h-3 text-primary font-bold" />
                      </div>
                      <p className="text-sm sm:text-base text-charcoal">{point}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Who This Webinar Is For */}
          <section data-testid="who-is-for-section" className="py-16 sm:py-24 bg-sage/30 border-y border-secondary/10">
            <div className="max-w-7xl mx-auto px-4 sm:px-8">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="text-xs font-bold text-gold uppercase tracking-widest block mb-2">TARGET PARTICIPANTS</span>
                <h3 className="text-2xl sm:text-3xl font-serif text-primary">This Webinar Is For You If...</h3>
                <div className="w-16 h-0.5 bg-gold mx-auto mt-4"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Card 1: Who you are */}
                <div className="card-premium p-6 rounded-2xl bg-ivory space-y-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
                    <Activity className="w-6 h-6 text-primary" />
                  </div>
                  <h4 className="text-lg font-serif font-bold text-primary">Your Profile</h4>
                  <ul className="space-y-3 text-left">
                    {["IT & Software Professionals", "Corporate / Desk-Based Professionals", "Work-from-Home Professionals", "Managers & Team Leaders", "Business Owners & Entrepreneurs"].map((it, idx) => (
                      <li key={idx} className="text-sm text-mutedTeal flex items-center gap-2">
                        <Check className="w-4 h-4 text-gold" /> {it}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Card 2: What you want to solve */}
                <div className="card-premium p-6 rounded-2xl bg-ivory space-y-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
                    <ShieldAlert className="w-6 h-6 text-primary" />
                  </div>
                  <h4 className="text-lg font-serif font-bold text-primary">Your Pain Points</h4>
                  <ul className="space-y-3 text-left">
                    {["Pain, tingling, or radiating numbness into the leg", "Stiffness and severely restricted movement", "Difficulty sitting, walking, bending, or working comfortably", "Fear of worsening symptoms or surgery advice", "Dependence on short-term pain pills"].map((pain, idx) => (
                      <li key={idx} className="text-sm text-mutedTeal flex items-center gap-2">
                        <Check className="w-4 h-4 text-gold" /> {pain}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Card 3: What you desire */}
                <div className="card-premium p-6 rounded-2xl bg-ivory space-y-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center border border-primary/20">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <h4 className="text-lg font-serif font-bold text-primary">Your Desired Outcomes</h4>
                  <ul className="space-y-3 text-left">
                    {["Understand the connection between sciatica & spinal compression", "Learn safe, specific NeuroYoga postures with no equipment", "Improve daily spine hygiene at your desk", "Move comfortably, confidently & pain-free", "Follow a simple, highly-structured daily path"].map((goal, idx) => (
                      <li key={idx} className="text-sm text-mutedTeal flex items-center gap-2">
                        <Check className="w-4 h-4 text-gold" /> {goal}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Video Placeholder & What You Will Learn */}
          <section className="py-16 sm:py-24 bg-ivory">
            <div className="max-w-7xl mx-auto px-4 sm:px-8">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="text-xs font-bold text-gold uppercase tracking-widest block mb-2">VIDEO INTRODUCTION</span>
                <h3 className="text-2xl sm:text-3xl font-serif text-primary">Watch This Before You Register</h3>
                <div className="w-16 h-0.5 bg-gold mx-auto mt-4"></div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                {/* Video Placeholder Box */}
                <div className="lg:col-span-6" data-testid="video-placeholder">
                  <div className="relative rounded-2xl overflow-hidden border-2 border-gold/45 shadow-xl aspect-video bg-charcoal group">
                    <img 
                      src="https://images.unsplash.com/photo-1552196563-55cd4e45efb3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHx5b2dhJTIwc3BpbmUlMjBkZWNvbXByZXNzaW9uJTIwc3RyZXRjaCUyMG1lZGljYWwlMjBob2xpc3RpYyUyMHdlbGxuZXNzfGVufDB8fHx8MTc4ODk0ODQzNnww&ixlib=rb-4.1.0&q=85"
                      alt="Pooja Chadha Spine Masterclass Intro Thumbnail"
                      className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                      <button 
                        onClick={openRegistrationModal}
                        className="w-16 h-16 rounded-full bg-primary text-ivory border border-gold/50 flex items-center justify-center shadow-lg group-hover:bg-gold group-hover:text-charcoal transition-all duration-300 transform group-hover:scale-110"
                      >
                        <Play className="w-6 h-6 ml-1 fill-current" />
                      </button>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 bg-primary/80 backdrop-blur-sm px-4 py-2.5 rounded-lg text-left">
                      <p className="text-xs font-bold text-gold uppercase tracking-widest">Masterclass Video Teaser</p>
                      <p className="text-xs text-ivory">Why traditional stretching might fail your discs, explained by Pooja Chadha.</p>
                    </div>
                  </div>
                  <p className="text-xs text-mutedTeal text-center mt-3 font-medium">
                    🎥 In this short video, Pooja Chadha explains why this webinar matters, who it is for, and what you will learn.
                  </p>
                </div>

                {/* What you will learn */}
                <div className="lg:col-span-6 space-y-6 text-left">
                  <h4 className="text-xl font-serif font-bold text-primary">Key Knowledge Chapters Covered:</h4>
                  <div className="space-y-4">
                    {[
                      "Understand the real physiological causes behind your lower-back or sciatica-related discomfort.",
                      "Discover the deep spine-nerve connection and why traditional exercises can sometimes worsen nerve pressure.",
                      "Learn safe, clinical NeuroYoga movements you can practice with zero equipment.",
                      "Know exactly which everyday sitting, bending, and posture habits support long-term spine preservation.",
                      "Leave with a clear, step-by-step action blueprint for daily movement freedom."
                    ].map((step, idx) => (
                      <div key={idx} className="flex gap-4 items-start">
                        <div className="w-7 h-7 rounded-full bg-primary text-ivory border border-gold/40 flex items-center justify-center text-xs font-bold font-serif flex-shrink-0">
                          {idx + 1}
                        </div>
                        <p className="text-sm sm:text-base text-charcoal">{step}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Interactive Spine Pressure Simulator */}
          <section className="py-16 sm:py-24 bg-sage/40 border-y border-secondary/15">
            <div className="max-w-7xl mx-auto px-4 sm:px-8">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="text-xs font-bold text-gold uppercase tracking-widest block mb-2">ANATOMICAL SIMULATOR</span>
                <h3 className="text-2xl sm:text-3xl font-serif text-primary">Interactive Spinal Compression vs. Decompression Visualizer</h3>
                <p className="text-sm text-mutedTeal mt-2">Select different daily states below to see how disc pressure and nerve tension react in real time.</p>
                <div className="w-16 h-0.5 bg-gold mx-auto mt-4"></div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                {/* Simulator Interactive Column */}
                <div className="lg:col-span-7 bg-ivory border border-gold/30 rounded-3xl p-6 sm:p-8 space-y-6 shadow-md text-left">
                  <h4 className="text-lg font-serif font-bold text-primary">Click an Everyday State:</h4>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <button 
                      onClick={() => setSimulatorState("SITTING")}
                      data-testid="spine-simulator-toggle-sitting"
                      className={`px-4 py-3 rounded-xl border text-sm font-semibold transition-all ${simulatorState === "SITTING" ? "bg-red-50 border-red-500 text-red-700 shadow-sm" : "bg-ivory border-primary/10 text-mutedTeal hover:bg-sage/10"}`}
                    >
                      💻 Sitting at Desk (Prolonged)
                    </button>
                    <button 
                      onClick={() => setSimulatorState("LIFTING")}
                      data-testid="spine-simulator-toggle-lifting"
                      className={`px-4 py-3 rounded-xl border text-sm font-semibold transition-all ${simulatorState === "LIFTING" ? "bg-red-100 border-red-600 text-red-900 shadow-sm" : "bg-ivory border-primary/10 text-mutedTeal hover:bg-sage/10"}`}
                    >
                      🏋️ Heavy Bending & Lifting
                    </button>
                    <button 
                      onClick={() => setSimulatorState("DECOMPRESSION")}
                      data-testid="spine-simulator-toggle-decompression"
                      className={`px-4 py-3 rounded-xl border text-sm font-semibold transition-all ${simulatorState === "DECOMPRESSION" ? "bg-emerald-50 border-emerald-500 text-emerald-700 shadow-sm" : "bg-ivory border-primary/10 text-mutedTeal hover:bg-emerald-50/10"}`}
                    >
                      🧘 Neuro-Spine Decompression™
                    </button>
                  </div>

                  <div className="p-5 rounded-2xl bg-sage/20 border border-secondary/10 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-mutedTeal uppercase tracking-wider">Simulated Disc Pressure:</span>
                      <span data-testid="spine-simulator-psi-display" className={`text-xl font-bold font-serif ${simulatorState === "DECOMPRESSION" ? "text-emerald-600" : "text-red-600"}`}>
                        {simulatorState === "SITTING" && "150 PSI"}
                        {simulatorState === "LIFTING" && "220 PSI"}
                        {simulatorState === "DECOMPRESSION" && "30 PSI"}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-mutedTeal uppercase tracking-wider">Nerve Compression Risk:</span>
                      <span className={`text-sm font-bold uppercase ${simulatorState === "DECOMPRESSION" ? "text-emerald-600" : "text-red-500"}`}>
                        {simulatorState === "SITTING" && "High Tension Warning"}
                        {simulatorState === "LIFTING" && "Severe Pinched Alert"}
                        {simulatorState === "DECOMPRESSION" && "Fully Decompressed & Relieved"}
                      </span>
                    </div>

                    <div className="h-2 w-full bg-secondary/15 rounded-full overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 ${simulatorState === "SITTING" ? "w-[70%] bg-red-400" : simulatorState === "LIFTING" ? "w-[95%] bg-red-600 animate-pulse" : "w-[15%] bg-emerald-500"}`}
                      ></div>
                    </div>

                    <p className="text-sm text-charcoal leading-relaxed pt-2">
                      {simulatorState === "SITTING" && "Prolonged sitting traps gravity inside your lower back, squishing the L4-L5/L5-S1 disc outwards. Discs dry out, causing recurring dull aches and chronic stiffness."}
                      {simulatorState === "LIFTING" && "Bending forward or heavy loaded movements under poor biomechanics compresses vertebrae tightly. Discs bulge rapidly, pinching the nearby sciatic nerve roots and sending shooting leg pain."}
                      {simulatorState === "DECOMPRESSION" && "Fusing specific NeuroYoga traction postures actively de-gravitizes the spine. Verterbrae separate gently, allowing re-hydration of the discs and instantly clearing sciatic nerve pathways."}
                    </p>
                  </div>
                </div>

                {/* Simulated spine visual column */}
                <div className="lg:col-span-5 flex flex-col items-center justify-center bg-ivory rounded-3xl border border-gold/25 p-8 shadow-sm">
                  <span className="text-xs font-bold text-gold uppercase tracking-widest block mb-4">Anatomical Response Map</span>
                  
                  {/* Spine visualization mockup using pure SVG */}
                  <div className="w-36 h-64 relative bg-primary/5 rounded-xl border border-primary/5 flex flex-col justify-center items-center gap-2 p-4">
                    {/* Vertebra block 1 */}
                    <div className="w-20 h-8 rounded bg-[#7A9B83] text-[9px] text-ivory font-bold flex items-center justify-center shadow-sm">L3 Vertebra</div>
                    {/* Disc 1 */}
                    <div className={`w-16 h-3 rounded-full transition-all duration-500 ${simulatorState === "SITTING" ? "bg-red-400 scale-y-[1.4]" : simulatorState === "LIFTING" ? "bg-red-600 scale-x-[1.25] scale-y-[1.7] animate-pulse" : "bg-emerald-400 scale-y-[0.8]"}`}></div>
                    
                    {/* Vertebra block 2 */}
                    <div className="w-20 h-8 rounded bg-[#7A9B83] text-[9px] text-ivory font-bold flex items-center justify-center shadow-sm">L4 Vertebra</div>
                    {/* Disc 2 (Intervertebral) */}
                    <div className={`w-16 h-3 rounded-full transition-all duration-500 ${simulatorState === "SITTING" ? "bg-red-400 scale-y-[1.4]" : simulatorState === "LIFTING" ? "bg-red-600 scale-x-[1.25] scale-y-[1.7] animate-pulse" : "bg-emerald-400 scale-y-[0.8]"}`}></div>
                    
                    {/* Vertebra block 3 */}
                    <div className="w-20 h-8 rounded bg-[#7A9B83] text-[9px] text-ivory font-bold flex items-center justify-center shadow-sm">L5 Vertebra</div>
                    
                    {/* Nerve Pathway Lines representation */}
                    <div className="absolute right-3 top-1/4 h-24 w-1 flex items-center justify-center">
                      <div className={`w-1.5 rounded-full transition-all ${simulatorState === "DECOMPRESSION" ? "h-24 bg-emerald-500 shadow-md" : "h-16 bg-red-500 animate-ping"}`}></div>
                      <span className="absolute left-3 text-[8px] whitespace-nowrap font-bold text-charcoal">Sciatic Nerve Pathway</span>
                    </div>
                  </div>

                  <div className="mt-6 text-center space-y-2">
                    <p className="text-xs font-bold text-primary">
                      {simulatorState === "SITTING" && "Result: Disc bulge + Posture tilt"}
                      {simulatorState === "LIFTING" && "Result: Severe L4-L5 herniation"}
                      {simulatorState === "DECOMPRESSION" && "Result: Optimum Spinal Alignment"}
                    </p>
                    <button 
                      onClick={openRegistrationModal}
                      className="text-xs font-bold text-gold hover:underline uppercase tracking-wider flex items-center gap-1 mx-auto mt-2"
                    >
                      Master This Method Live <ArrowRight className="w-3 h-3 text-gold" />
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </section>

          {/* Old Way vs New Way */}
          <section data-testid="old-new-way-section" className="py-16 sm:py-24 bg-ivory">
            <div className="max-w-7xl mx-auto px-4 sm:px-8">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="text-xs font-bold text-gold uppercase tracking-widest block mb-2">THERAPEUTIC COMPARISON</span>
                <h3 className="text-2xl sm:text-3xl font-serif text-primary">The Old Way vs. The New Way</h3>
                <div className="w-16 h-0.5 bg-gold mx-auto mt-4"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                {/* Old Way */}
                <div className="p-8 rounded-3xl bg-red-50/40 border border-red-200/50 space-y-6 text-left">
                  <div className="inline-block px-3 py-1 rounded-full bg-red-100 text-red-800 text-xs font-bold uppercase tracking-wider">
                    The Old Way (Symptom-masking)
                  </div>
                  <ul className="space-y-4">
                    {[
                      { title: "Painkillers & Anti-inflammatories", desc: "Temporarily dulls the chronic back ache but does not resolve spinal disc compression." },
                      { title: "Complete / Prolonged Rest", desc: "Weakens supporting core muscles over time and triggers deeper postural stiffness." },
                      { title: "Generic Physiotherapy", desc: "Applying identical stretching or exercises without matching individual MRI condition." },
                      { title: "Cortisone Injections", desc: "Pain relief for only a few weeks, leaving the root mechanical compression intact." },
                      { title: "High-risk Spine Surgery", desc: "Often recommended hastily for disc bulges and sciatica, despite non-surgical alternatives." }
                    ].map((item, idx) => (
                      <li key={idx} className="space-y-1">
                        <span className="flex items-center gap-2 text-sm sm:text-base font-bold text-red-900">
                          <X className="w-4 h-4 text-red-600 flex-shrink-0" /> {item.title}
                        </span>
                        <p className="text-xs sm:text-sm text-red-700/80 pl-6">{item.desc}</p>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* New Way */}
                <div className="p-8 rounded-3xl bg-emerald-50/20 border border-emerald-200/50 space-y-6 text-left">
                  <div className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider">
                    The New Way (Root Mechanical Decompression)
                  </div>
                  <ul className="space-y-4">
                    {[
                      { title: "Targeted Neuro Decompression", desc: "Core approach for safe traction, relieving trapped nerve roots without surgery." },
                      { title: "Therapeutic NeuroYoga Postures", desc: "Carefully calibrated sequences configured safely to suit lumbar-spine issues." },
                      { title: "Spine & Nerve Mobility Routines", desc: "Gradually enhances fluid pathways, clearing sciatica-related pain." },
                      { title: "Everyday Movement Correction", desc: "Restructuring sitting, bending, and work breaks to avoid spinal load." },
                      { title: "Holistic Home-Care Habits", desc: "Consistent, safe lifestyle habits that support lifelong spine alignment." }
                    ].map((item, idx) => (
                      <li key={idx} className="space-y-1">
                        <span className="flex items-center gap-2 text-sm sm:text-base font-bold text-emerald-900">
                          <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" /> {item.title}
                        </span>
                        <p className="text-xs sm:text-sm text-emerald-800/80 pl-6">{item.desc}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Neuro-Spine Decompression Method Philosophy */}
          <section className="py-16 sm:py-24 bg-sage/30 border-y border-secondary/15">
            <div className="max-w-7xl mx-auto px-4 sm:px-8">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="text-xs font-bold text-gold uppercase tracking-widest block mb-2">METHODOLOGY BLUEPRINT</span>
                <h3 className="text-2xl sm:text-3xl font-serif text-primary">The Neuro-Spine Decompression Method™ Philosophy</h3>
                <div className="w-16 h-0.5 bg-gold mx-auto mt-4"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                <div data-testid="philosophy-step-1" className="p-8 bg-ivory rounded-2xl border border-gold/25 space-y-4 text-left">
                  <div className="w-10 h-10 rounded-full bg-primary text-ivory flex items-center justify-center font-bold font-serif text-sm">01</div>
                  <h4 className="text-lg font-serif font-bold text-primary">Decompress</h4>
                  <p className="text-sm text-mutedTeal leading-relaxed">
                    Reduce excessive structural load, physical compression, and gravitational stress around the vertebrae and sciatic nerves through safe, targeted Neuro Decompression traction techniques.
                  </p>
                </div>
                <div data-testid="philosophy-step-2" className="p-8 bg-ivory rounded-2xl border border-gold/25 space-y-4 text-left">
                  <div className="w-10 h-10 rounded-full bg-primary text-ivory flex items-center justify-center font-bold font-serif text-sm">02</div>
                  <h4 className="text-lg font-serif font-bold text-primary">Rebalance</h4>
                  <p className="text-sm text-mutedTeal leading-relaxed">
                    Integrate personalized NeuroYoga postures and correction exercises to improve flexibility, spine mobility, and posture, re-adjusting asymmetric spinal shifts safely.
                  </p>
                </div>
                <div data-testid="philosophy-step-3" className="p-8 bg-ivory rounded-2xl border border-gold/25 space-y-4 text-left">
                  <div className="w-10 h-10 rounded-full bg-primary text-ivory flex items-center justify-center font-bold font-serif text-sm">03</div>
                  <h4 className="text-lg font-serif font-bold text-primary">Restore & Maintain</h4>
                  <p className="text-sm text-mutedTeal leading-relaxed">
                    Formulate structured, easy-to-repeat daily habits, sleep posture guidelines, and sitting mechanics to maintain fully realigned spine and nerve health permanently.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* The 3 Secrets You Will Learn */}
          <section className="py-16 sm:py-24 bg-ivory">
            <div className="max-w-7xl mx-auto px-4 sm:px-8">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="text-xs font-bold text-gold uppercase tracking-widest block mb-2">CURRICULUM PILLARS</span>
                <h3 className="text-2xl sm:text-3xl font-serif text-primary">The 3 Secrets You Will Learn In This Webinar</h3>
                <div className="w-16 h-0.5 bg-gold mx-auto mt-4"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                <div data-testid="secret-card-1" className="card-premium p-8 rounded-2xl space-y-4 text-left">
                  <span className="text-xs font-bold text-gold uppercase tracking-widest block">Secret 01</span>
                  <h4 className="text-lg font-serif font-bold text-primary">The Spine–Nerve Connection</h4>
                  <p className="text-sm text-mutedTeal">
                    Why understanding the deep connection between your vertebral column alignment and neural nerve branches matters. Discover what exactly triggers leg numbness or sciatica, and how standard workouts could be aggravating it.
                  </p>
                </div>
                <div data-testid="secret-card-2" className="card-premium p-8 rounded-2xl space-y-4 text-left">
                  <span className="text-xs font-bold text-gold uppercase tracking-widest block">Secret 02</span>
                  <h4 className="text-lg font-serif font-bold text-primary">The Decompression Principle</h4>
                  <p className="text-sm text-mutedTeal">
                    How specific movement routines and postural traction support better vertebral disc mobility and decompression. Learn mechanical postures to relieve structural pressure right at home, with no specialized machines or gear.
                  </p>
                </div>
                <div data-testid="secret-card-3" className="card-premium p-8 rounded-2xl space-y-4 text-left">
                  <span className="text-xs font-bold text-gold uppercase tracking-widest block">Secret 03</span>
                  <h4 className="text-lg font-serif font-bold text-primary">Everyday Spine Protection Formula</h4>
                  <p className="text-sm text-mutedTeal">
                    How small, simple adjustments to your everyday sitting setups, walking habits, hydration, and sleep positions protect long-term spine alignment and prevent future stiffness from creeping back in.
                  </p>
                </div>
              </div>

              <div className="mt-12 text-center">
                <button
                  onClick={openRegistrationModal}
                  className="btn-premium bg-primary text-ivory font-bold uppercase tracking-wider text-sm px-8 py-4 rounded-full border border-gold shadow-md inline-flex items-center gap-2"
                >
                  Register for FREE Webinar <ArrowRight className="w-4 h-4 text-gold" />
                </button>
              </div>
            </div>
          </section>

          {/* Benefits of Implementing This */}
          <section data-testid="benefits-section" className="py-16 sm:py-24 bg-sage/30 border-y border-secondary/15">
            <div className="max-w-4xl mx-auto px-4 sm:px-8 text-left space-y-8">
              <div className="text-center">
                <span className="text-xs font-bold text-gold uppercase tracking-widest block mb-2">EXPECTED TRANSFORMATION</span>
                <h3 className="text-2xl sm:text-3xl font-serif text-primary text-center">What Can Change When You Implement This?</h3>
                <div className="w-16 h-0.5 bg-gold mx-auto mt-4"></div>
              </div>

              <div className="p-8 bg-ivory rounded-3xl border border-gold/25 space-y-6">
                {[
                  "Clear, objective understanding of the primary factors contributing to your recurring lower-back or sciatica pain.",
                  "Actionable capability to practice gentle NeuroYoga & safe traction stretches safely from your home or desk.",
                  "Reduced chronic lower-back stiffness, restoring natural flexibility in your daily activities.",
                  "Clear, practical guidelines on sitting, walking, and ergonomic posture to prevent disc strain during long screen hours.",
                  "Reclaimed everyday lifestyle comfort, and a structured non-invasive roadmap to long-term spinal longevity."
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4 items-start">
                    <CheckCircle2 className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm sm:text-base text-charcoal leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Detailed Biography about Pooja Chadha */}
          <section id="bio" data-testid="bio-section" className="py-16 sm:py-24 bg-ivory">
            <div className="max-w-7xl mx-auto px-4 sm:px-8">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="text-xs font-bold text-gold uppercase tracking-widest block mb-2">MEET YOUR SPECIALIST</span>
                <h3 className="text-2xl sm:text-3xl font-serif text-primary">About Pooja Chadha</h3>
                <div className="w-16 h-0.5 bg-gold mx-auto mt-4"></div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start max-w-6xl mx-auto">
                <div className="lg:col-span-5 relative">
                  <div className="rounded-3xl overflow-hidden border border-gold/45 shadow-lg bg-ivory">
                    <img 
                      src="https://images.emergent.sh/de9dd7e3-d96c-4ea0-914d-0dc5158af4c7/0.png" 
                      alt="Pooja Chadha guiding spinal traction postures" 
                      data-testid="bio-image"
                      className="w-full h-auto object-cover max-h-[450px]"
                      onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1706353399656-210cca727a33?auto=format&fit=crop&w=800&q=80"; }}
                    />
                    <div className="p-4 bg-primary text-ivory text-center">
                      <p className="font-serif text-sm font-bold text-gold">Pooja Neuro Yoga</p>
                      <p className="text-[10px] uppercase font-semibold text-ivory/80">Pain Free Spine Hub Founder</p>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7 space-y-6 text-left">
                  <p className="text-sm sm:text-base text-charcoal leading-relaxed font-medium">
                    Pooja Chadha is a renowned Neuro-Spine Decompression Expert, NeuroYoga Specialist, Yoga Trainer and Senior Wellness Naturopathy Therapist. 
                    For over 27 years, she has dedicated her offline and online practices to helping desk-bound corporate professionals, homemakers, and individuals recover from lumbar-spine issues including sciatica, slip disc, disc bulge, disc desiccation, and nerve compression.
                  </p>

                  <div className="space-y-4">
                    <h4 className="text-base font-serif font-bold text-primary uppercase tracking-wider">Academic & Professional Credentials:</h4>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs sm:text-sm text-mutedTeal">
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold" /> B.Com, Punjab University (1983)</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold" /> Diploma in Yoga Teacher Training</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold" /> AYUSH Level 4 Ministry Certification</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold" /> Master's in Nerve Decompression</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold" /> NSDC Senior Naturopathy Therapist</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-gold" /> Patanjali Haridwar Recognized</li>
                    </ul>
                  </div>

                  <div className="p-6 bg-sage/20 border border-secondary/15 rounded-2xl space-y-3">
                    <h4 className="text-sm font-serif font-bold text-primary uppercase tracking-wider">Mission & Philosophy:</h4>
                    <p className="text-xs sm:text-sm text-charcoal leading-relaxed">
                      "I believe that people deserve to understand their spine and take an active role in their spinal health rather than living in fear of pain, dependency, or unnecessary surgical interventions. My mission is to help millions of desk professionals recover comfortable movement and build a community of trained Back Pain Reversal Coaches worldwide."
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials Section */}
          <section id="testimonials" className="py-16 sm:py-24 bg-sage/30 border-y border-secondary/15">
            <div className="max-w-7xl mx-auto px-4 sm:px-8">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="text-xs font-bold text-gold uppercase tracking-widest block mb-2">VERIFIED STUDENT RESULTS</span>
                <h3 className="text-2xl sm:text-3xl font-serif text-primary">What Students Say</h3>
                <p className="text-sm text-mutedTeal mt-1">Real reviews from busy professionals who followed the 90-day Neuro-Spine Decompression program.</p>
                <div className="w-16 h-0.5 bg-gold mx-auto mt-4"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {/* Testimonial 1 */}
                <div data-testid="testimonial-card-1" className="card-premium p-8 bg-ivory rounded-2xl flex flex-col justify-between text-left space-y-6">
                  <div className="space-y-4">
                    <div className="flex gap-1 text-gold">
                      {"★".repeat(5).split("").map((s, i) => <span key={i}>{s}</span>)}
                    </div>
                    <p className="text-sm text-charcoal leading-relaxed italic">
                      "I suffered from severe L4-L5 and L5-S1 disc bulges and was recommended surgery. Seeking a non-surgical solution, I joined Pooja's program. In just 3 months of personalized NeuroYoga, nerve decompression and posture setup, I experienced incredible improvement. Today, I can easily lift and pick up my 4-year-old daughter—something pain made impossible before!"
                    </p>
                  </div>
                  <div>
                    <h5 className="font-serif font-bold text-primary text-sm sm:text-base">Sudhir Jaiswal</h5>
                    <p className="text-xs text-mutedTeal">IT Professional • Noida</p>
                  </div>
                </div>

                {/* Testimonial 2 */}
                <div data-testid="testimonial-card-2" className="card-premium p-8 bg-ivory rounded-2xl flex flex-col justify-between text-left space-y-6">
                  <div className="space-y-4">
                    <div className="flex gap-1 text-gold">
                      {"★".repeat(5).split("").map((s, i) => <span key={i}>{s}</span>)}
                    </div>
                    <p className="text-sm text-charcoal leading-relaxed italic">
                      "Due to a severe slip disc, my lower-back stiffness was so high my posture was tilted sideways. Doctors told me surgery was my only way out. I shared my MRI with Pooja and joined her 90-day protocol. Within 45 days, my pelvic alignment was perfectly straight, stiffness disappeared, and I am now enjoying a completely pain-free life without medicines."
                    </p>
                  </div>
                  <div>
                    <h5 className="font-serif font-bold text-primary text-sm sm:text-base">Vikas Mishra</h5>
                    <p className="text-xs text-mutedTeal">Bank Manager • Gurugram</p>
                  </div>
                </div>

                {/* Testimonial 3 */}
                <div data-testid="testimonial-card-3" className="card-premium p-8 bg-ivory rounded-2xl flex flex-col justify-between text-left space-y-6">
                  <div className="space-y-4">
                    <div className="flex gap-1 text-gold">
                      {"★".repeat(5).split("").map((s, i) => <span key={i}>{s}</span>)}
                    </div>
                    <p className="text-sm text-charcoal leading-relaxed italic">
                      "I am a retired school principal, and severe right-sided sciatica pain from my hip to my heel made household chores, walking, and sleeping absolute torture. Ayurveda and Homeopathy failed, and I was terrified of surgery. After following Pooja's NeuroYoga nerve-release routines for 90 days, I resumed all daily activities completely pain-free!"
                    </p>
                  </div>
                  <div>
                    <h5 className="font-serif font-bold text-primary text-sm sm:text-base">Saroj Sharma</h5>
                    <p className="text-xs text-mutedTeal">Retired Principal • Kurukshetra</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Free Resources Section */}
          <section className="py-16 sm:py-24 bg-ivory">
            <div className="max-w-7xl mx-auto px-4 sm:px-8">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="text-xs font-bold text-gold uppercase tracking-widest block mb-2">EXCLUSIVE WEBINAR BONUS</span>
                <h3 className="text-2xl sm:text-3xl font-serif text-primary">Free Resources You Will Get In The Webinar</h3>
                <p className="text-sm text-mutedTeal mt-1">Sign up today and attend the masterclass live to unlock these premium spinal resources.</p>
                <div className="w-16 h-0.5 bg-gold mx-auto mt-4"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                <div data-testid="resource-card-1" className="card-premium p-8 bg-ivory rounded-2xl border border-gold/30 text-left space-y-4">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-gold" />
                  </div>
                  <h4 className="text-lg font-serif font-bold text-primary">1. 7-Day Lower-Back Relief Routine</h4>
                  <p className="text-xs font-bold text-gold uppercase tracking-wider">Value: ₹1,999 • FREE</p>
                  <p className="text-sm text-mutedTeal">
                    A simple, gentle 10-minute daily morning movement routine to unlock morning stiffness, restore flexibility, and build muscular support for comfortable daily sitting.
                  </p>
                </div>

                <div data-testid="resource-card-2" className="card-premium p-8 bg-ivory rounded-2xl border border-gold/30 text-left space-y-4">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center">
                    <Award className="w-6 h-6 text-gold" />
                  </div>
                  <h4 className="text-lg font-serif font-bold text-primary">2. Desk-Worker Spine & Posture Reset</h4>
                  <p className="text-xs font-bold text-gold uppercase tracking-wider">Value: ₹1,999 • FREE</p>
                  <p className="text-sm text-mutedTeal">
                    Step-by-step posture corrections, active micro-break movements, and ergonomic desk setup templates to relieve lumbar pressure during heavy, continuous keyboard hours.
                  </p>
                </div>

                <div data-testid="resource-card-3" className="card-premium p-8 bg-ivory rounded-2xl border border-gold/30 text-left space-y-4">
                  <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-gold" />
                  </div>
                  <h4 className="text-lg font-serif font-bold text-primary">3. Nerve-Friendly Daily Habits Checklist</h4>
                  <p className="text-xs font-bold text-gold uppercase tracking-wider">Value: ₹2,999 • FREE</p>
                  <p className="text-sm text-mutedTeal">
                    A highly practical daily reminder checklist covering sleep biomechanics, hydration milestones, correct bending posture, and simple habits to ensure your spine remains decompressed.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Who Should & Who Should Not Attend */}
          <section data-testid="should-attend-section" className="py-16 sm:py-24 bg-sage/30 border-y border-secondary/15">
            <div className="max-w-7xl mx-auto px-4 sm:px-8">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="text-xs font-bold text-gold uppercase tracking-widest block mb-2">DECISION MATRIX</span>
                <h3 className="text-2xl sm:text-3xl font-serif text-primary">Should You Attend This Webinar?</h3>
                <div className="w-16 h-0.5 bg-gold mx-auto mt-4"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto text-left">
                {/* Attend */}
                <div className="p-8 rounded-3xl bg-ivory border border-emerald-200/60 space-y-4">
                  <h4 className="text-lg font-serif font-bold text-emerald-800 flex items-center gap-2">
                    <Check className="w-5 h-5 text-emerald-600" /> You Should Attend If:
                  </h4>
                  <ul className="space-y-3 text-sm text-mutedTeal">
                    <li>✓ You are an IT, software, corporate, or desk-based professional struggling with lower-back stiffness or leg pain.</li>
                    <li>✓ You want better spinal and nerve health through natural, non-surgical movements.</li>
                    <li>✓ You are willing to invest 10 minutes daily in simple posture & decompression routines.</li>
                    <li>✓ You resonate with a structured, step-by-step clinical approach.</li>
                    <li>✓ You want expert instruction, anatomical clarity, and practical guidance.</li>
                  </ul>
                </div>

                {/* Not Attend */}
                <div className="p-8 rounded-3xl bg-ivory border border-red-200/60 space-y-4">
                  <h4 className="text-lg font-serif font-bold text-red-800 flex items-center gap-2">
                    <X className="w-5 h-5 text-red-600" /> You Should NOT Attend If:
                  </h4>
                  <ul className="space-y-3 text-sm text-mutedTeal">
                    <li>✕ You are looking for instant, magical, one-minute "fixes" for chronic slip discs.</li>
                    <li>✕ You are not willing to build a consistent daily spinal-care routine.</li>
                    <li>✕ You do not respect structured, non-invasive rehabilitation concepts.</li>
                    <li>✕ You are seeking personalized clinical treatment/medical consultations on a live webinar.</li>
                    <li>✕ You are not open to taking active responsibility for your own physical recovery.</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Summary and Value Stack */}
          <section data-testid="value-stack-section" className="py-16 sm:py-24 bg-ivory">
            <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center space-y-8">
              <div className="text-center">
                <span className="text-xs font-bold text-gold uppercase tracking-widest block mb-2">COMPLETE OFFER VALUE</span>
                <h3 className="text-2xl sm:text-3xl font-serif text-primary">What You Are Getting For FREE</h3>
                <div className="w-16 h-0.5 bg-gold mx-auto mt-4"></div>
              </div>

              {/* Value stack card container with gold trace beam glow */}
              <div className="p-8 bg-ivory rounded-3xl border-2 border-gold/45 shadow-lg space-y-6 text-left max-w-2xl mx-auto">
                <h4 className="text-lg font-serif font-bold text-primary text-center">Spine Reset Curriculum Value Stack</h4>
                
                <div className="space-y-4 divide-y divide-gold/15">
                  <div className="flex justify-between items-center py-2 text-sm sm:text-base text-charcoal">
                    <span>🧘 The Spine Reset Masterclass Live Session</span>
                    <span className="font-serif font-bold text-primary">₹14,999</span>
                  </div>
                  <div className="flex justify-between items-center py-2 text-sm sm:text-base text-charcoal">
                    <span>📋 7-Day Lower-Back Relief Routine Blueprint</span>
                    <span className="font-serif font-bold text-primary">₹1,999</span>
                  </div>
                  <div className="flex justify-between items-center py-2 text-sm sm:text-base text-charcoal">
                    <span>💻 Desk-Worker Spine & Posture Reset Guide</span>
                    <span className="font-serif font-bold text-primary">₹1,999</span>
                  </div>
                  <div className="flex justify-between items-center py-2 text-sm sm:text-base text-charcoal">
                    <span>📅 Nerve-Friendly Daily Habits Checklist</span>
                    <span className="font-serif font-bold text-primary">₹2,999</span>
                  </div>
                  <div className="flex justify-between items-center pt-4 text-base sm:text-lg font-bold text-primary">
                    <span>Total Real Value:</span>
                    <span className="font-serif text-mutedTeal line-through">₹21,996</span>
                  </div>
                  <div className="flex justify-between items-center pt-3 text-lg sm:text-xl font-bold text-emerald-700">
                    <span>Your Investment Today:</span>
                    <span className="font-serif bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-sm sm:text-base uppercase tracking-wider">FREE</span>
                  </div>
                </div>

                <div className="pt-4 text-center">
                  <button
                    onClick={openRegistrationModal}
                    className="btn-premium bg-primary text-ivory font-bold uppercase tracking-wider text-sm px-8 py-4 rounded-full border border-gold w-full"
                  >
                    Register for FREE Webinar
                  </button>
                  <p className="text-[10px] text-mutedTeal mt-2 text-center">
                    🔒 Registration is absolutely free. No credit card required.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Frequently Asked Questions */}
          <section id="faq" data-testid="faq-section" className="py-16 sm:py-24 bg-sage/30 border-y border-secondary/15">
            <div className="max-w-4xl mx-auto px-4 sm:px-8">
              <div className="text-center max-w-3xl mx-auto mb-16">
                <span className="text-xs font-bold text-gold uppercase tracking-widest block mb-2">REASSURANCE & CLARITY</span>
                <h3 className="text-2xl sm:text-3xl font-serif text-primary">Frequently Asked Questions</h3>
                <div className="w-16 h-0.5 bg-gold mx-auto mt-4"></div>
              </div>

              <div className="space-y-4 max-w-3xl mx-auto text-left">
                {[
                  { q: "Is this webinar really free?", a: "Yes. This live webinar is 100% free to attend. You only need to complete the registration qualification filter and join the Zoom link on time." },
                  { q: "Who is this webinar for?", a: "This masterclass is designed specifically for 35–55-year-old IT, software, corporate, and desk-based professionals struggling with recurrent lower-back discomfort, stiffness, or sciatic nerve pain." },
                  { q: "I am a beginner with no yoga experience. Can I attend?", a: "Absolutely! The Neuro-Spine Decompression Method™ is built on gentle, anatomical mobility principles with absolutely no requirement of pre-existing flexibility or yoga practice." },
                  { q: "Will this webinar be recorded and shared?", a: "No. Recording will not be shared. This is an interactive live masterclass with real-time anatomical simulations, and you must attend live to capture full details and resources." },
                  { q: "How will I get the Zoom joining link?", a: "Immediately after qualified registration through TagMango, you will receive confirmation details directly on your email and WhatsApp with your private joining link." },
                  { q: "What should I keep ready for the session?", a: "Keep a notebook, pen, water bottle, and a quiet place ready to sit comfortably for 90 minutes of educational insights." },
                  { q: "Will there be any paid offer at the end?", a: "At the end of this educational session, Pooja will share details of her next structured 90-day spinal alignment community program for those interested in a guided roadmap. Joining is fully optional." },
                  { q: "Is this webinar considered medical advice?", a: "No. This webinar is purely for education and awareness. For severe medical concerns, spinal fractures, or post-surgery situations, always consult with a qualified physician before initiating any new movement protocol." },
                  { q: "What if I am not sure whether this is right for me?", a: "Review our 'Should You Attend' section and click any CTA to answer the 2-step qualification pop-up. If it matches your desk lifestyle, you are highly qualified to attend." }
                ].map((faq, idx) => (
                  <div 
                    key={idx} 
                    data-testid={`faq-accordion-item-${idx + 1}`}
                    aria-expanded={openFaqIndex === idx ? "true" : "false"}
                    data-state={openFaqIndex === idx ? "open" : "closed"}
                    className="p-5 bg-ivory rounded-2xl border border-gold/25 shadow-sm space-y-3 cursor-pointer"
                    onClick={() => handleFaqToggle(idx)}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-serif font-bold text-primary text-sm sm:text-base">{faq.q}</span>
                      <button className="text-gold font-bold">
                        {openFaqIndex === idx ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                      </button>
                    </div>
                    
                    {openFaqIndex === idx && (
                      <p className="text-xs sm:text-sm text-mutedTeal leading-relaxed pt-2 border-t border-gold/10">
                        {faq.a}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Final Footer CTA Block */}
          <section className="py-20 bg-ivory relative overflow-hidden">
            <div className="absolute top-0 inset-x-0 h-[1px] bg-gradient-to-r from-transparent via-gold to-transparent"></div>
            <div className="max-w-4xl mx-auto px-4 sm:px-8 text-center space-y-8 relative">
              <span className="text-xs font-bold text-gold uppercase tracking-widest block">RECLAIM COMFORTABLE MOVEMENT</span>
              <h3 className="text-3xl sm:text-4xl font-serif text-primary leading-tight">Ready To Take The Next Step?</h3>
              
              <p className="text-sm sm:text-base text-mutedTeal max-w-xl mx-auto">
                If you are a corporate desk professional seeking to decompress your spine and regain confidence and movement, this free webinar will help you understand the next step clearly.
              </p>

              <div className="space-y-3 max-w-md mx-auto pt-2">
                <button
                  onClick={openRegistrationModal}
                  data-testid="footer-register-cta"
                  className="btn-premium bg-primary text-ivory font-bold uppercase tracking-wider text-sm px-8 py-4 rounded-full border border-gold w-full shadow-lg"
                >
                  Register for FREE Webinar
                </button>
                <p className="text-[10px] text-mutedTeal">
                  Free session • Limited seats • Live on 19 September 11AM IST
                </p>
              </div>
            </div>
          </section>

        </div>
      </main>

      {/* Sticky Bottom Bar (displayed only after long scroll is unfolded) */}
      <AnimatePresence>
        {showLongScroll && (
          <motion.div 
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            data-testid="sticky-bottom-bar"
            className="fixed bottom-0 inset-x-0 bg-primary text-ivory border-t-2 border-gold py-4 px-6 z-50 shadow-[0_-10px_30px_rgba(15,92,94,0.15)] flex flex-col sm:flex-row items-center justify-between gap-4"
          >
            <div className="text-center sm:text-left">
              <span className="text-xs font-bold text-gold uppercase tracking-wider block">PAINFREE SPINE MASTERCLASS</span>
              <span className="text-[10px] text-ivory/80 flex items-center gap-1.5 justify-center sm:justify-start">
                ⏱ Webinar starts in: {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s
              </span>
            </div>

            <div className="flex items-center gap-3">
              <span className="hidden lg:inline text-xs text-gold font-medium">Free session • Limited spots remaining</span>
              <button
                onClick={openRegistrationModal}
                data-testid="sticky-register-cta"
                className="btn-premium bg-gold text-charcoal hover:bg-gold/90 font-bold px-6 py-2.5 rounded-full text-xs uppercase tracking-wider"
              >
                Register FREE Webinar
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer Bar */}
      <footer className="bg-primary text-ivory py-12 px-4 sm:px-8 border-t border-gold/30">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left space-y-1">
            <p className="font-serif text-lg font-bold tracking-wider text-gold">Neuro-Spine Wellness Hub</p>
            <p className="text-xs text-ivory/70">
              Copyright 2026 Neuro-Spine Wellness Hub. All rights reserved.
            </p>
            <p className="text-[10px] text-ivory/50">
              Education and awareness only. For physical medical emergencies, seek direct doctor guidance.
            </p>
          </div>

          <div className="flex flex-col items-center md:items-end gap-3">
            <div className="flex items-center gap-4 text-xs font-semibold text-gold">
              <button 
                onClick={() => setActiveLegalModal("PRIVACY")}
                data-testid="footer-privacy-link"
                className="hover:underline"
              >
                Privacy Policy
              </button>
              <span>|</span>
              <button 
                onClick={() => setActiveLegalModal("TERMS")}
                data-testid="footer-terms-link"
                className="hover:underline"
              >
                Terms and Conditions
              </button>
            </div>
            <p className="text-xs text-ivory/70 flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5 text-gold" />
              Contact: poojachadha333@gmail.com
            </p>
          </div>
        </div>
      </footer>

      {/* Registration Modal Overlay */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/70 backdrop-blur-sm">
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              data-testid="registration-modal"
              className="bg-ivory border-2 border-gold max-w-xl w-full rounded-3xl overflow-hidden shadow-2xl relative"
            >
              {/* Close button */}
              <button 
                onClick={() => setIsModalOpen(false)}
                className="absolute top-4 right-4 text-primary hover:text-gold p-1.5 rounded-full bg-primary/5 hover:bg-primary/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="p-8 space-y-6">
                
                {/* Step 1: Qualification Filter */}
                {modalStep === 1 && (
                  <div className="space-y-6 text-left">
                    <div className="space-y-2">
                      <span className="text-xs font-bold text-gold uppercase tracking-widest block">Step 1 of 3: Pre-qualification</span>
                      <h4 className="text-xl font-serif font-bold text-primary">Which option describes you best?</h4>
                      <p className="text-xs text-mutedTeal">
                        We screen participants to ensure high quality peer discussion and relevant curriculum matching.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <button
                        onClick={() => handleQualification("YES")}
                        data-testid="filter-option-yes"
                        className="w-full text-left p-5 rounded-2xl border border-primary/20 bg-primary/5 hover:bg-primary/10 hover:border-gold/60 transition-all space-y-2 block"
                      >
                        <span className="text-xs font-bold text-primary block">✓ YES, I AM AN IT / CORP DESK PROFESSIONAL</span>
                        <span className="text-xs sm:text-sm text-charcoal block leading-relaxed">
                          experiencing lower-back discomfort, stiffness, or nerve-related challenges, and I am serious about better spine health, comfortable movement, and daily freedom.
                        </span>
                      </button>

                      <button
                        onClick={() => handleQualification("NO")}
                        data-testid="filter-option-no"
                        className="w-full text-left p-5 rounded-2xl border border-primary/10 bg-ivory hover:bg-red-50/20 hover:border-red-300 transition-all space-y-1 block"
                      >
                        <span className="text-xs font-bold text-red-700 block">✕ NO, I AM NOT LOOKING FOR THIS</span>
                        <span className="text-xs sm:text-sm text-mutedTeal block">
                          I do not experience these symptoms or I am not ready to take active lifestyle steps right now.
                        </span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2: Profile Selection */}
                {modalStep === 2 && (
                  <form onSubmit={handleDetailsSubmit} className="space-y-6 text-left">
                    <div className="space-y-2">
                      <span className="text-xs font-bold text-gold uppercase tracking-widest block">Step 2 of 3: Tailor Your Experience</span>
                      <h4 className="text-xl font-serif font-bold text-primary">Tell us a bit about your lifestyle</h4>
                      <p className="text-xs text-mutedTeal">
                        This information allows Pooja Chadha to address specific desk challenges during the live session.
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-primary uppercase tracking-wider block">What best describes you?</label>
                        <select
                          required
                          value={profile}
                          onChange={(e) => setProfile(e.target.value)}
                          data-testid="filter-profile-select"
                          className="w-full p-4 rounded-xl border border-primary/20 bg-ivory text-charcoal text-sm font-semibold focus:outline-none focus:border-gold"
                        >
                          <option value="">Select your profession</option>
                          <option value="IT">IT / Software Professional</option>
                          <option value="Corporate">Corporate / Desk-Based Professional</option>
                          <option value="WFH">Work-from-Home Professional</option>
                          <option value="Manager">Manager / Team Leader</option>
                          <option value="Owner">Business Owner / Entrepreneur</option>
                        </select>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-primary uppercase tracking-wider block">What is your biggest current challenge?</label>
                        <select
                          required
                          value={challenge}
                          onChange={(e) => setChallenge(e.target.value)}
                          data-testid="filter-challenge-select"
                          className="w-full p-4 rounded-xl border border-primary/20 bg-ivory text-charcoal text-sm font-semibold focus:outline-none focus:border-gold"
                        >
                          <option value="">Select your challenge</option>
                          <option value="Hours">Long hours of sitting and screen time</option>
                          <option value="Stiffness">Recurring lower-back discomfort and stiffness</option>
                          <option value="Nerve">Nerve-related discomfort or sciatica</option>
                          <option value="Posture">Difficulty maintaining correct posture</option>
                          <option value="Movement">Lack of regular movement and exercise</option>
                          <option value="Productivity">Reduced productivity and concentration</option>
                          <option value="Worse">Fear of the problem getting worse</option>
                          <option value="Balance">Difficulty balancing work, health and personal life</option>
                        </select>
                      </div>
                    </div>

                    <button
                      type="submit"
                      data-testid="filter-continue-button"
                      className="btn-premium bg-primary text-ivory font-bold uppercase tracking-wider text-sm py-4 rounded-full border border-gold w-full text-center block"
                    >
                      Continue
                    </button>
                  </form>
                )}

                {/* Step 3: Divert / Confirmation Success Screen */}
                {modalStep === 3 && (
                  <div className="space-y-6 text-center py-6">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center justify-center mx-auto shadow-md">
                      <Check className="w-8 h-8 font-bold" />
                    </div>

                    <div className="space-y-2">
                      <span className="text-xs font-bold text-gold uppercase tracking-widest block">Step 3 of 3: Verification Success!</span>
                      <h4 className="text-2xl font-serif font-bold text-primary">Pre-Qualification Verified</h4>
                      <p className="text-sm text-mutedTeal max-w-md mx-auto">
                        You are highly qualified for this masterclass! We have prepared your slot on the official TagMango registration node.
                      </p>
                    </div>

                    {isRedirecting ? (
                      <div className="space-y-3 py-4">
                        <div className="w-10 h-10 border-4 border-primary border-t-gold rounded-full animate-spin mx-auto"></div>
                        <p className="text-xs text-mutedTeal animate-pulse">Connecting to secure TagMango gateway...</p>
                      </div>
                    ) : (
                      <div className="space-y-4 pt-4">
                        <a
                          href={tagmangoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="filter-redirect-button"
                          className="btn-premium bg-gold text-charcoal hover:bg-gold/90 font-bold uppercase tracking-wider text-sm py-4 px-8 rounded-full border border-gold inline-block w-full text-center shadow-lg"
                        >
                          Complete Official Registration
                        </a>
                        <p className="text-[10px] text-mutedTeal flex items-center gap-1.5 justify-center">
                          <Lock className="w-3.5 h-3.5 text-gold" />
                          TagMango Secured API Endpoint Integration
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Disqualification State */}
                {modalStep === 4 && (
                  <div className="space-y-6 text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-red-100 text-red-800 border border-red-300 flex items-center justify-center mx-auto">
                      <X className="w-8 h-8 font-bold" />
                    </div>

                    <div className="space-y-2">
                      <h4 className="text-xl font-serif font-bold text-primary">Not a Perfect Match</h4>
                      <p className="text-sm text-mutedTeal max-w-sm mx-auto leading-relaxed">
                        This webinar may not be the right fit for you right now. Pooja Chadha's masterclass is strictly optimized for professional desk workers experiencing lumbar stiffness. Thank you for your interest!
                      </p>
                    </div>

                    <button
                      onClick={() => setIsModalOpen(false)}
                      className="btn-premium bg-primary text-ivory font-bold uppercase tracking-wider text-xs py-3 px-6 rounded-full border border-gold"
                    >
                      Close Window
                    </button>
                  </div>
                )}

              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Legal Modals Overlays */}
      <AnimatePresence>
        {activeLegalModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/70 backdrop-blur-sm">
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-ivory border-2 border-gold max-w-3xl w-full rounded-3xl overflow-hidden shadow-2xl relative max-h-[85vh] flex flex-col"
            >
              <button 
                onClick={() => setActiveLegalModal(null)}
                className="absolute top-4 right-4 text-primary hover:text-gold p-1.5 rounded-full bg-primary/5 hover:bg-primary/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="p-8 overflow-y-auto space-y-6 text-left">
                {activeLegalModal === "PRIVACY" ? (
                  <div className="space-y-4 text-sm text-charcoal">
                    <h4 className="text-2xl font-serif font-bold text-primary">Privacy Policy</h4>
                    <p className="text-xs text-mutedTeal">Last updated: 1 September 2026</p>
                    <div className="w-16 h-0.5 bg-gold"></div>

                    <p>
                      At <strong>Neuro-Spine Wellness Hub</strong>, we respect your privacy. This Privacy Policy explains how we collect, use and protect your information when you register for our webinar or interact with our website.
                    </p>

                    <h5 className="font-serif font-bold text-primary text-base">10.1 Information We Collect</h5>
                    <p>We may collect your name, email address, WhatsApp number, selected profile/profession, current spinal challenges, and webinar attendance analytics.</p>

                    <h5 className="font-serif font-bold text-primary text-base">10.2 How We Use Your Information</h5>
                    <p>We use your details to confirm your webinar registration, transmit Zoom credentials, send timely reminders, offer free educational resources, and share future program updates.</p>

                    <h5 className="font-serif font-bold text-primary text-base">10.3 Communication Consent</h5>
                    <p>By registering, you explicitly agree to receive educational details and notifications from Neuro-Spine Wellness Hub via Email, WhatsApp, and SMS.</p>

                    <h5 className="font-serif font-bold text-primary text-base">10.4 Data Protection</h5>
                    <p>We employ high-grade security steps to safeguard your personal data. We never sell your personal contact information to unrelated third parties.</p>

                    <h5 className="font-serif font-bold text-primary text-base">10.5 Third-Party Tools</h5>
                    <p>We utilize third-party platforms (like TagMango, Zoom, and Mailers) for registrations, reminders, hosting, and secure data storage.</p>

                    <p className="font-semibold text-primary">Contact Email: poojachadha333@gmail.com</p>
                  </div>
                ) : (
                  <div className="space-y-4 text-sm text-charcoal">
                    <h4 className="text-2xl font-serif font-bold text-primary">Terms and Conditions</h4>
                    <p className="text-xs text-mutedTeal">Last updated: 1 September 2026</p>
                    <div className="w-16 h-0.5 bg-gold"></div>

                    <p>
                      By registering for and attending the <strong>Neuro-Spine Reset Masterclass</strong>, you agree to comply with the following educational terms:
                    </p>

                    <h5 className="font-serif font-bold text-primary text-base">11.1 Educational Purpose</h5>
                    <p>All webinar contents, slides, videos, resources, and checklists are designed and provided for educational and informational purposes only.</p>

                    <h5 className="font-serif font-bold text-primary text-base">11.2 No Medical Claims</h5>
                    <p>
                      The webinar does not provide physical medical diagnoses, clinical treatment, or custom orthopedic cures. If you have chronic conditions, spinal implants, post-surgery, or pregnancy concerns, consult with a qualified spine doctor or physician before beginning any physical stretches or NeuroYoga sequences.
                    </p>

                    <h5 className="font-serif font-bold text-primary text-base">11.3 No Guaranteed Results</h5>
                    <p>Therapeutic outcomes vary per person based on individual effort, spinal condition, lifestyle consistency, and physical baseline. We make no guarantees of specific recoveries.</p>

                    <h5 className="font-serif font-bold text-primary text-base">11.4 Participant Responsibility</h5>
                    <p>You assume complete personal responsibility for your body, safety, actions, and daily movements practiced during or after the masterclass.</p>

                    <h5 className="font-serif font-bold text-primary text-base">11.5 Intellectual Property</h5>
                    <p>All training sequences, trademarks, logo branding, and method designs are the exclusive intellectual property of Neuro-Spine Wellness Hub.</p>

                    <p className="font-semibold text-primary">Contact Email: poojachadha333@gmail.com</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
